/**
 * Mapbox Directions API クライアント (歩行経路)。
 *
 * 仕様: https://docs.mapbox.com/api/navigation/directions/
 * - profile: walking (歩行ナビ)
 * - geometries=geojson で LineString座標列を取得
 * - overview=full で精細な経路を取得 (デフォルトは簡略)
 *
 * 失敗時はエラーをthrow。呼び出し元で fallback (直線等) を選ぶ。
 */
import { MAPBOX_TOKEN } from './mapbox';
import { fetchWithTimeout } from '@/lib/net/fetch-timeout';
import type { LngLat } from '@/types/property';

export class DirectionsError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'DirectionsError';
  }
}

export interface WalkingRouteResult {
  /** GeoJSON LineString の coordinates */
  coordinates: Array<[number, number]>;
  /** 経路全体の歩行距離 (メートル) */
  distance: number;
  /** 想定所要時間 (秒) */
  duration: number;
}

interface DirectionsApiResponse {
  routes?: Array<{
    distance: number;
    duration: number;
    geometry: { type: 'LineString'; coordinates: Array<[number, number]> };
  }>;
}

/**
 * Directions API 呼び出しの共通ボイラープレート。
 * トークン確認 → URL構築 → fetch → エラーハンドリング → 経路配列へ変換。
 * 経路が1本も無ければ throw (公開3関数とも同一のエラーメッセージ)。
 */
async function requestDirections(
  waypoints: LngLat[],
  opts: { alternatives?: boolean } = {},
): Promise<WalkingRouteResult[]> {
  if (!MAPBOX_TOKEN) {
    throw new DirectionsError(
      'Mapboxトークンが未設定です。.envの VITE_MAPBOX_PUBLIC_TOKEN を設定してください。',
    );
  }
  if (waypoints.length < 2) {
    throw new DirectionsError('経由地が不足しています (2点以上必要)');
  }
  const coords = waypoints.map((w) => `${w.lng},${w.lat}`).join(';');
  const url = new URL(
    `https://api.mapbox.com/directions/v5/mapbox/walking/${coords}`,
  );
  url.searchParams.set('geometries', 'geojson');
  url.searchParams.set('overview', 'full');
  if (opts.alternatives) {
    url.searchParams.set('alternatives', 'true');
  }
  url.searchParams.set('access_token', MAPBOX_TOKEN);

  let response: Response;
  try {
    response = await fetchWithTimeout(url.toString());
  } catch (e) {
    throw new DirectionsError('Mapbox Directions APIへの接続に失敗しました', e);
  }
  if (!response.ok) {
    throw new DirectionsError(
      `Mapbox Directions APIがエラーを返しました (${response.status})`,
    );
  }
  const json = (await response.json()) as DirectionsApiResponse;
  const routes = json.routes ?? [];
  if (routes.length === 0) {
    throw new DirectionsError('経路が見つかりませんでした');
  }
  return routes.map((r) => ({
    coordinates: r.geometry.coordinates,
    distance: r.distance,
    duration: r.duration,
  }));
}

/**
 * 2地点間の歩行経路を取得。
 */
export async function fetchWalkingRoute(
  start: LngLat,
  end: LngLat,
): Promise<WalkingRouteResult> {
  const routes = await requestDirections([start, end]);
  return routes[0]!;
}

/**
 * 2地点間の歩行経路(複数候補)を取得。alternatives=true で最大3本。
 * 経路候補選択機能で使う。見つからなければ throw。
 */
export async function fetchWalkingRoutes(
  start: LngLat,
  end: LngLat,
): Promise<WalkingRouteResult[]> {
  return requestDirections([start, end], { alternatives: true });
}

/**
 * 複数の経由地を順に通る歩行経路を1本取得する (start;via;...;end)。
 * 徒歩プロファイルは alternatives がほぼ常に1本しか返さないため、経由地を挟んで
 * 「別ルートに迂回した候補」を人工的に作るのに使う。経由地は最寄り歩道にスナップされる。
 * 経路が見つからなければ throw。
 */
export async function fetchWalkingRouteThrough(
  waypoints: LngLat[],
): Promise<WalkingRouteResult> {
  const routes = await requestDirections(waypoints);
  return routes[0]!;
}
